import { useEffect, useRef, useState } from 'react';
import { useComandosDoJogo } from '@/hooks/useComandosDoJogo';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpCircle, Crown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '@/contexts/StoreContext';
import { useCart } from '@/contexts/CartContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cartService } from '@/lib/services/cart.service';
import { vipService, tempoDoVip, type MeuVip, type OfertaDeUpgrade, type OpcaoDeUpgrade } from '@/lib/services/vip.service';

const LOCALE: Record<string, string> = { pt: 'pt-BR', en: 'en-US', es: 'es-ES' };
const COR: Record<MeuVip['status'], string> = {
  ACTIVE: 'border-emerald-500/40 text-emerald-500',
  STORED: 'border-amber-500/40 text-amber-500',
  PAUSED: 'border-slate-500/40 text-slate-400',
  FROZEN: 'border-sky-500/40 text-sky-500',
  EXPIRED: 'border-border text-muted-foreground',
  REMOVED: 'border-border text-muted-foreground',
};

/**
 * "Meus VIPs" no perfil do jogador (módulo VIP).
 *
 * Location: src/components/vip
 * Why: o VIP mora no site; o jogador vê aqui o mesmo que vê no /vip do
 * servidor: o que vale, até quando, o que está guardado e o que acabou.
 * Ativar e trocar ficam no jogo, onde o VIP vale. O upgrade (trocar o VIP que
 * vale por um maior pagando a diferença) é comprado aqui, pelo carrinho: o
 * preço vem do servidor e é refeito no pedido. `?upgrade=1` (link do /vip
 * upgrade no jogo) rola até as opções.
 */
export function MeusVips() {
  const { t, i18n } = useTranslation();
  const comandos = useComandosDoJogo();
  const { store } = useStore();
  const locale = LOCALE[i18n.language?.slice(0, 2)] ?? 'pt-BR';
  const [vips, setVips] = useState<MeuVip[] | null>(null);
  const [erro, setErro] = useState(false);
  const [oferta, setOferta] = useState<OfertaDeUpgrade | null>(null);
  const [escolhendo, setEscolhendo] = useState<string | null>(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshCart } = useCart();
  const upgradeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!store?.id) return;
    vipService
      .meus(store.id)
      .then(setVips)
      .catch(() => {
        setVips([]);
        setErro(true);
      });
    vipService
      .upgrades(store.id)
      .then(setOferta)
      .catch(() => setOferta(null));
  }, [store?.id]);

  // Veio do /vip upgrade no jogo: leva direto às opções.
  useEffect(() => {
    if (params.get('upgrade') && oferta?.options.length) upgradeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [params, oferta]);

  const preco = (valor: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: oferta?.currency || 'BRL' }).format(valor);

  const escolher = async (o: OpcaoDeUpgrade) => {
    if (!store?.id || !oferta?.from) return;
    const chave = `${o.vipDefinitionId}:${o.permanent}`;
    setEscolhendo(chave);
    try {
      await cartService.addVipUpgrade(store.id, { fromPlayerVipId: oferta.from.playerVipId, toVipDefinitionId: o.vipDefinitionId, permanent: o.permanent });
      await refreshCart();
      navigate('/carrinho');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('store.vip.upgrade.unavailable'));
    } finally {
      setEscolhendo(null);
    }
  };

  if (vips === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (erro) return <p className="py-10 text-center text-sm text-muted-foreground">{t('store.vip.load_error')}</p>;

  if (vips.length === 0) {
    return (
      <div className="py-12 text-center" data-testid="meus-vips-vazio">
        <Crown className="mx-auto mb-4 h-14 w-14 text-muted-foreground opacity-50" />
        <p className="mb-2 text-lg font-medium text-muted-foreground">{t('store.vip.empty_title')}</p>
        <p className="text-sm text-muted-foreground">{t('store.vip.empty_desc', { vip: comandos.vip })}</p>
        <Link to="/loja">
          <Button className="mt-4">{t('store.vip.go_shop')}</Button>
        </Link>
      </div>
    );
  }

  const data = (iso: string) => new Date(iso).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });

  const upgrades = oferta?.from && oferta.options.length ? (
    <div ref={upgradeRef} className="mb-4 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="meus-vips-upgrade">
      <div className="mb-1 flex items-center gap-2">
        <ArrowUpCircle className="h-5 w-5 text-primary" />
        <span className="font-display font-semibold text-foreground">{t('store.vip.upgrade.title', { vip: oferta.from.name })}</span>
      </div>
      <p className="mb-3 text-sm text-muted-foreground">
        {oferta.from.permanent ? t('store.vip.upgrade.desc_permanent') : t('store.vip.upgrade.desc')}
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {oferta.options.map((o) => {
          const chave = `${o.vipDefinitionId}:${o.permanent}`;
          return (
            <div key={chave} className="flex items-center justify-between gap-3 rounded-lg border border-border/50 bg-card/60 p-3" data-testid="upgrade-opcao">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: o.color }} />
                  <span className="truncate font-medium text-foreground">{o.name}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {o.permanent
                    ? t('store.vip.upgrade.option_permanent')
                    : t(oferta.from?.onlineTime ? 'store.vip.upgrade.option_time_online' : 'store.vip.upgrade.option_time', { tempo: tempoDoVip(o.seconds) })}
                </p>
              </div>
              <Button size="sm" disabled={escolhendo !== null} onClick={() => escolher(o)} data-testid="upgrade-escolher">
                {escolhendo === chave ? <Loader2 className="h-4 w-4 animate-spin" /> : preco(o.price)}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <div>
    {upgrades}
    <div className="grid gap-3 md:grid-cols-2" data-testid="meus-vips">
      {vips.map((v) => {
        const aberto = v.status !== 'EXPIRED' && v.status !== 'REMOVED';
        return (
          <div key={v.id} className="rounded-xl border border-border/50 bg-card/60 p-4" data-testid="meu-vip">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: v.color }} />
                <span className="font-display font-semibold text-foreground">{v.name}</span>
              </div>
              <Badge variant="outline" className={COR[v.status]}>
                {t(`store.vip.status.${v.status}`)}
              </Badge>
            </div>
            <div className="mt-2 space-y-1 text-sm text-muted-foreground">
              {aberto && v.permanent && <p>{t('store.vip.permanent')}</p>}
              {aberto && !v.permanent && (
                <p>{t(v.onlineTime ? 'store.vip.remaining_online' : 'store.vip.remaining', { tempo: tempoDoVip(v.remainingSeconds) })}</p>
              )}
              {v.status === 'ACTIVE' && v.expiresAt && <p>{t('store.vip.until', { data: data(v.expiresAt) })}</p>}
              {!aberto && v.endedAt && <p>{t('store.vip.ended', { data: data(v.endedAt) })}</p>}
              {v.status === 'STORED' && <p className="text-xs">{t('store.vip.hint_stored', { vip: comandos.vip })}</p>}
              {v.status === 'PAUSED' && <p className="text-xs">{t('store.vip.hint_paused')}</p>}
              {aberto && v.onlineTime && <p className="text-xs">{t('store.vip.hint_online')}</p>}
            </div>
          </div>
        );
      })}
    </div>
    </div>
  );
}
